// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { CustomFieldOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import moment from 'moment';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';

export interface CustomFieldOptionsInputProps {
    value?: any;
    onChange?: (value: any) => void;
    customField: CustomFieldOutput;
}

export const CustomFieldOptionsInput = ({
    value = '',
    onChange,
    customField,
}: CustomFieldOptionsInputProps): ReactElement | null => {
    const i = useLocalMessage();

    const { type, options = [] } = customField;

    if (options?.length > 0) {
        if (type === 'integer') {
            return (
                <THSelect
                    placeholder={i('customField.use.placeholder.select')}
                    value={value}
                    onChange={onChange}
                    options={options.map((option) => ({ label: option, value: parseInt(option) }))}
                />
            );
        }

        return (
            <THSelect
                placeholder={i('customField.use.placeholder.select')}
                value={value}
                onChange={onChange}
                options={options.map((option) => ({ label: option, value: option }))}
            />
        );
    }

    if (['float', 'integer'].includes(type)) {
        return (
            <THInputNumber
                placeholder={i('customField.use.placeholder.input')}
                value={value}
                onChange={onChange}
                className="block-width"
                step={type === 'integer' ? 1 : 0.01}
            />
        );
    }

    if (type === 'string') {
        return <THTextArea placeholder={i('customField.use.placeholder.input')} value={value} onChange={onChange} />;
    }

    if (type === 'date') {
        const valueProcessed = moment(parseInt(value, 10));

        return (
            <THDatePicker
                placeholder={i('customField.use.placeholder.input')}
                className="block-width"
                value={valueProcessed.isValid() ? valueProcessed : value}
                onChange={(value): void => {
                    if (value) {
                        const valueProcessed = value.toDate().getTime();
                        onChange?.(valueProcessed);
                    }
                }}
            />
        );
    }

    if (type === 'boolean') {
        return (
            <THSelect
                placeholder={i('customField.use.placeholder.select')}
                value={value?.toString()}
                onChange={(value) => onChange?.(value === 'true' ? true : false)}
                options={['true', 'false'].map((bool) => ({ label: bool, value: bool }))}
            />
        );
    }

    return null;
};
